'use client'

import { type KeyboardEvent, type ReactNode, useEffect, useRef, useState } from 'react'
import usePrevious from 'react-use/lib/usePrevious'

import cn from 'clsx'
import { HyperLink } from '@/components/hyper-link/hyper-link'
import { Text } from '@/components/Text/Text'
import { useThrottledScroll } from '@/utils/useThrottledScroll'
import { Keyboard } from '@/utils/keyboard'
import { checkHighlightedHeading, type TOCHeading } from '@/utils/tocHelpers'
import { ChevronDown } from '@doist/icons/brand'
import css from './GuideMenu.module.css'
import { Box } from '@/components/Box/Box'

const PERCENT_MAX_HEIGHT = 0.5

export type GuideMenuProps = {
    theme?: 'todoist' | 'twist'
    headings: TOCHeading[]
    title?: string
    defaultTopLinkTitle?: string
    trackActiveHeading?: boolean
    children?: ReactNode
    className?: string
}

export function GuideMenu({
    theme = 'todoist',
    title = 'Table of Contents',
    headings,
    className,
    defaultTopLinkTitle,
    trackActiveHeading = true,
    children,
    ...rest
}: GuideMenuProps) {
    const [isOpen, setOpen] = useState(false)
    const prevIsOpen = usePrevious(isOpen)
    const [activeHeading, setActiveHeading] = useState<TOCHeading>()
    const [selectedIndex, setSelectedIndex] = useState(1)
    const $list = useRef<HTMLUListElement>(null)
    const $trigger = useRef<HTMLElement>(null)

    useEffect(() => {
        /** Expand or collapse the list. */
        let maxHeight = '0'
        if (isOpen) {
            const threshold = window.innerHeight * PERCENT_MAX_HEIGHT
            let expectedHeight = $list.current?.scrollHeight ?? 0
            if (expectedHeight > threshold) {
                expectedHeight = threshold
            }
            maxHeight = `${expectedHeight}px`
        } else if (!isOpen && prevIsOpen === true) {
            if ($trigger.current) {
                $trigger.current.focus()
            }

            setSelectedIndex(1)
        }

        if ($list.current) {
            $list.current.style.maxHeight = maxHeight
        }

        /** Block scroll on background */
        document.body.style.overflow = isOpen ? 'hidden' : 'auto'
    }, [isOpen, prevIsOpen])

    useEffect(() => {
        if (!isOpen) {
            return
        }

        if ($list.current) {
            const elementAtIndex: HTMLAnchorElement | null = $list.current.querySelector(
                `li:nth-child(${selectedIndex}) a`,
            )

            if (elementAtIndex) {
                elementAtIndex.focus()
            }
        }
    }, [isOpen, selectedIndex])

    useThrottledScroll(() => {
        function updateActiveHeading() {
            if (!trackActiveHeading) {
                return
            }
            const heading = checkHighlightedHeading(headings)
            setActiveHeading(heading)
        }
        updateActiveHeading()
        return updateActiveHeading
    }, [headings, trackActiveHeading])

    useEffect(() => {
        if (!trackActiveHeading) {
            setActiveHeading(undefined)
        }
    }, [trackActiveHeading])

    function keydownHandler(evt: KeyboardEvent<HTMLDivElement>) {
        if (!isOpen) {
            return
        }

        if (evt.key === Keyboard.Escape) {
            setOpen(false)
            return
        }

        if (evt.key === Keyboard.ArrowUp) {
            const newIndex = selectedIndex - 1
            setSelectedIndex(newIndex < 1 ? headings.length : newIndex)
            return
        }

        if (evt.key === Keyboard.ArrowDown) {
            const newIndex = selectedIndex + 1
            setSelectedIndex(newIndex > headings.length ? 1 : newIndex)
            return
        }

        if (evt.key === Keyboard.Home) {
            setSelectedIndex(1)
            return
        }

        if (evt.key === Keyboard.End) {
            setSelectedIndex(headings.length)
        }
    }

    function triggerKeyDownHandler(evt: KeyboardEvent<HTMLButtonElement>) {
        switch (evt.key) {
            case Keyboard.ArrowDown:
                setOpen(true)
                return
            case Keyboard.ArrowUp:
                setOpen(true)
                setSelectedIndex(headings.length)
        }
    }

    return (
        <>
            {isOpen && (
                <Box
                    aria-label="Close"
                    className={css.backgroundOverlay}
                    onClick={() => setOpen(false)}
                />
            )}

            <Box
                className={cn(
                    css.guideMenu,
                    { [css.open]: isOpen, [css.twist]: theme === 'twist' },
                    className,
                )}
                {...rest}
                onKeyDown={keydownHandler}
            >
                {headings.length > 0 && (
                    <div className={css.menuContainer}>
                        <Box
                            tag="button"
                            type="button"
                            className={css.heading}
                            onClick={() => setOpen(!isOpen)}
                            ref={$trigger}
                            onKeyDown={triggerKeyDownHandler}
                            aria-haspopup
                            aria-expanded={isOpen}
                        >
                            <Text
                                tag="span"
                                size="minor-lg"
                                weight={theme === 'todoist' ? 'semibold' : undefined}
                            >
                                {!isOpen && activeHeading ? activeHeading.title : title}
                            </Text>
                            <Box className={css.icon}>
                                <ChevronDown width={15} aria-hidden />
                            </Box>
                        </Box>
                        {!isOpen && children}
                    </div>
                )}

                <ul ref={$list} className={css.list} role="menu">
                    {headings.map((heading) => (
                        <li
                            id={`mobile-guide-${heading.id}`}
                            key={heading.id}
                            className={cn({
                                [css.active]: activeHeading && activeHeading.id === heading.id,
                            })}
                            role="none"
                        >
                            <HyperLink
                                size="body-xs"
                                href={`#${heading.id}`}
                                onClick={() => {
                                    setOpen(false)
                                    window.location.hash = heading.id
                                }}
                                role="menuitem"
                                tabIndex={-1}
                            >
                                {heading.title}
                            </HyperLink>
                        </li>
                    ))}
                </ul>
            </Box>
        </>
    )
}
